import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext, useLocation } from "react-router-dom";

/** Paging the same register is safe; changing the edited subject is navigation. */
function subjectKey(value: string): string {
  const url = new URL(value, window.location.origin);
  return `${url.pathname}|${url.searchParams.get("tab") ?? "roles"}|${url.searchParams.get("role") ?? "ADMIN"}|${url.searchParams.get("user") ?? ""}`;
}

/** BrowserRouter has no data-router blocker. Guard its navigator and browser history together. */
export function useAccessLeaveGuard(enabled: boolean, frozen: boolean, confirmLeave: () => Promise<boolean>, discard: () => void) {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const location = useLocation();
  const latest = useRef({ enabled, frozen, confirmLeave, discard });
  latest.current = { enabled, frozen, confirmLeave, discard };
  const current = useRef({ key: subjectKey(`${location.pathname}${location.search}`), index: window.history.state?.idx as number | undefined });
  const pending = useRef(false);
  useEffect(() => { current.current = { key: subjectKey(`${location.pathname}${location.search}`), index: window.history.state?.idx as number | undefined }; }, [location]);

  useEffect(() => {
    const push = navigator.push.bind(navigator);
    const replace = navigator.replace.bind(navigator);
    const go = navigator.go.bind(navigator);
    let correcting = false;
    let acceptedPop = false;
    let abandoned = false;
    let afterCorrection: (() => void) | null = null;

    async function proceed(action: () => void) {
      if (pending.current || latest.current.frozen) return;
      pending.current = true;
      try {
        if (await latest.current.confirmLeave() && !abandoned) {
          latest.current.enabled = false;
          latest.current.discard();
          action();
        }
      } finally { pending.current = false; }
    }

    const guardedPush: typeof navigator.push = (...args) => {
      const to = args[0];
      const path = typeof to === "string" ? to : `${to.pathname ?? location.pathname}${to.search ?? ""}`;
      if (latest.current.enabled && new URL(path, window.location.origin).pathname !== location.pathname) void proceed(() => push(...args));
      else push(...args);
    };
    const guardedReplace: typeof navigator.replace = (...args) => {
      const to = args[0];
      const path = typeof to === "string" ? to : `${to.pathname ?? location.pathname}${to.search ?? ""}`;
      if (latest.current.enabled && new URL(path, window.location.origin).pathname !== location.pathname) void proceed(() => replace(...args));
      else replace(...args);
    };
    const guardedGo: typeof navigator.go = (delta) => {
      if (latest.current.enabled) void proceed(() => { acceptedPop = true; go(delta); });
      else go(delta);
    };
    navigator.push = guardedPush;
    navigator.replace = guardedReplace;
    navigator.go = guardedGo;

    const onPop = (event: PopStateEvent) => {
      if (correcting) { correcting = false; event.stopImmediatePropagation(); const resume = afterCorrection; afterCorrection = null; resume?.(); return; }
      if (acceptedPop) { acceptedPop = false; return; }
      if (!latest.current.enabled || subjectKey(window.location.href) === current.current.key) return;
      const target = event.state?.idx;
      if (typeof target !== "number" || typeof current.current.index !== "number") return;
      const delta = current.current.index - target;
      if (delta === 0) return;
      event.stopImmediatePropagation();
      correcting = true;
      window.history.go(delta);
      void proceed(() => {
        const resume = () => { acceptedPop = true; window.history.go(-delta); };
        if (correcting) afterCorrection = resume; else resume();
      });
    };
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (latest.current.enabled) { event.preventDefault(); event.returnValue = ""; }
    };
    window.addEventListener("popstate", onPop, true);
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      abandoned = true;
      if (navigator.push === guardedPush) navigator.push = push;
      if (navigator.replace === guardedReplace) navigator.replace = replace;
      if (navigator.go === guardedGo) navigator.go = go;
      window.removeEventListener("popstate", onPop, true);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [navigator, location.pathname]);
}
